import React, { useState } from 'react';
import { X, Wrench, AlertTriangle, CheckCircle, Info, Settings } from 'lucide-react';
import { 
  repairFloorPlanFile, 
  generateRepairReport, 
  DEFAULT_REPAIR_OPTIONS,
  type FileRepairResult, 
  type RepairOptions 
} from '../utils/fileRepairTool';
import { 
  repairWallOwnership, 
  needsWallOwnershipRepair, 
  formatRepairSummary,
  type WallOwnershipRepairResult 
} from '../utils/wallOwnershipRepairTool';
import type { Wall, FloorPlanElement, WallWithTempId } from '../types';

interface FileRepairDialogProps {
  isOpen: boolean;
  onClose: () => void;
  walls: Wall[];
  elements: FloorPlanElement[];
  roomCustomLabels: Record<string, string>;
  floorPlans: Record<number, Wall[]>; // Added for wall ownership repair
  onApplyRepair: (repairedWalls: WallWithTempId[], repairedElements: FloorPlanElement[], repairedRoomLabels: Record<string, string>) => void;
  onApplyOwnershipRepair?: (repairedFloorPlans: Record<number, Wall[]>) => void; // Added for wall ownership repair
}

const FileRepairDialog: React.FC<FileRepairDialogProps> = ({
  isOpen,
  onClose,
  walls,
  elements,
  roomCustomLabels,
  floorPlans,
  onApplyRepair,
  onApplyOwnershipRepair
}) => {
  const [repairResult, setRepairResult] = useState<FileRepairResult | null>(null);
  const [ownershipRepairResult, setOwnershipRepairResult] = useState<WallOwnershipRepairResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isRepairingOwnership, setIsRepairingOwnership] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [repairOptions, setRepairOptions] = useState<RepairOptions>({
    ...DEFAULT_REPAIR_OPTIONS,
    // 🔧 CONSERVATIVE DEFAULTS: More conservative settings to preserve room labels and elements
    fixExtrusionIssues: false, // DISABLED: Prevents stable ID corruption that can lose room labels
    fixLegacyParentWallIds: false, // DISABLED: Prevents stable ID corruption that can lose room labels
    removeOrphanedWalls: false, // DISABLED: Prevents room loss
    removeDuplicateWalls: false, // DISABLED: Prevents room loss
    validateGeometry: false, // DISABLED: Prevents geometric changes that can affect room detection
    fixZeroLengthWalls: false, // DISABLED: Prevents room boundary changes
    completeIncompleteRooms: false, // DISABLED: Don't add walls during repair
    labelUnnamedRooms: false, // DISABLED: Don't auto-label during repair
    relocateElementsToValidFloors: false, // DISABLED: Keep elements on original floors
    // Keep these enabled as they're safe for preservation
    generateMissingTempIds: true,
    generateMissingElementIds: true,
    fixBrokenElementReferences: true,
    fixElementProperties: true,
    preserveAllElementTypes: true,
  });

  if (!isOpen) return null;

  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    try {
      // 🔧 PRESERVATION: Create backup of original room labels before repair
      const originalRoomLabelsBackup = { ...roomCustomLabels };
      
      // Run repair analysis (without applying changes yet)
      const result = repairFloorPlanFile(walls, elements, repairOptions, roomCustomLabels);
      
      // 🔧 PRESERVATION: Check if any room labels were lost during repair
      const labelsLostDuringRepair = Object.keys(originalRoomLabelsBackup).filter(
        originalKey => !result.repairedRoomLabels[originalKey]
      );
      
      if (labelsLostDuringRepair.length > 0) {
        console.warn(`[FileRepairDialog] 🔧 ROOM LABEL PRESERVATION: ${labelsLostDuringRepair.length} labels lost during repair, restoring...`, {
          lostLabels: labelsLostDuringRepair.map(key => ({ key, label: originalRoomLabelsBackup[key] }))
        });
        
        // Restore lost labels
        labelsLostDuringRepair.forEach(lostKey => {
          result.repairedRoomLabels[lostKey] = originalRoomLabelsBackup[lostKey];
        });
        
        // Add this as a fixed issue in the report
        result.issuesFixed.push(`Preserved ${labelsLostDuringRepair.length} room labels that would have been lost`);
      }
      
      setRepairResult(result);
    } catch (error) {
      console.error('Error analyzing file:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleApplyRepair = () => {
    if (!repairResult) return;
    
    onApplyRepair(
      repairResult.repairedWalls,
      repairResult.repairedElements,
      repairResult.repairedRoomLabels
    );
    onClose();
  };

  // 🏠 WALL OWNERSHIP REPAIR HANDLERS
  const handleAnalyzeOwnership = async () => {
    setIsRepairingOwnership(true);
    try {
      console.log('🔧 ANALYZING WALL OWNERSHIP...');
      
      // Check if repair is needed
      const needsRepair = needsWallOwnershipRepair(floorPlans);
      
      if (!needsRepair) {
        setOwnershipRepairResult({
          success: true,
          repairedFloors: [],
          totalWallsRepaired: 0,
          totalRoomsDetected: 0,
          errors: [],
          repairSummary: {}
        });
        return;
      }
      
      // Create a copy for repair analysis (don't modify original)
      const floorPlansCopy = JSON.parse(JSON.stringify(floorPlans));
      const result = repairWallOwnership(floorPlansCopy);
      
      setOwnershipRepairResult(result);
      
    } catch (error) {
      console.error('❌ Wall ownership analysis failed:', error);
      setOwnershipRepairResult({
        success: false,
        repairedFloors: [],
        totalWallsRepaired: 0,
        totalRoomsDetected: 0,
        errors: [`Analysis failed: ${error}`],
        repairSummary: {}
      });
    } finally {
      setIsRepairingOwnership(false);
    }
  };

  const handleApplyOwnershipRepair = () => {
    if (!ownershipRepairResult || !onApplyOwnershipRepair) return;
    
    // Apply the repair by updating floor plans
    const repairedFloorPlans = JSON.parse(JSON.stringify(floorPlans));
    repairWallOwnership(repairedFloorPlans);
    
    onApplyOwnershipRepair(repairedFloorPlans);
    onClose();
  };

  const handleReset = () => {
    setRepairResult(null);
    setRepairOptions({
      ...DEFAULT_REPAIR_OPTIONS,
      // 🔧 CONSERVATIVE DEFAULTS: Reset to conservative settings
      fixExtrusionIssues: false,
      fixLegacyParentWallIds: false,
      removeOrphanedWalls: false,
      removeDuplicateWalls: false,
      validateGeometry: false,
      fixZeroLengthWalls: false,
      completeIncompleteRooms: false,
      labelUnnamedRooms: false,
      relocateElementsToValidFloors: false,
      generateMissingTempIds: true,
      generateMissingElementIds: true,
      fixBrokenElementReferences: true,
      fixElementProperties: true,
      preserveAllElementTypes: true,
    });
  };

  const getSeverityColor = (issuesFound: number) => {
    if (issuesFound === 0) return 'text-green-600';
    if (issuesFound < 5) return 'text-blue-600';
    if (issuesFound < 10) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSeverityIcon = (issuesFound: number) => {
    if (issuesFound === 0) return <CheckCircle className="text-green-600" size={20} />;
    if (issuesFound < 10) return <Info className="text-blue-600" size={20} />;
    return <AlertTriangle className="text-yellow-600" size={20} />;
  };

  const getElementTypeBreakdown = (elements: FloorPlanElement[]): Record<string, number> => {
    const breakdown: Record<string, number> = {};
    elements.forEach(element => {
      breakdown[element.type] = (breakdown[element.type] || 0) + 1;
    });
    return breakdown;
  };

  const elementBreakdown = getElementTypeBreakdown(elements);
  const totalElements = elements.length;
  const elementTypes = Object.keys(elementBreakdown).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Wrench className="text-blue-600" size={24} />
            <h2 className="text-lg font-semibold text-gray-900">Comprehensive File Repair Tool</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {!repairResult ? (
            <>
              {/* File Overview */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3">Current File Overview</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium text-gray-900">Walls</div>
                    <div className="text-2xl font-bold text-blue-600">{walls.length}</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium text-gray-900">Elements</div>
                    <div className="text-2xl font-bold text-green-600">{totalElements}</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium text-gray-900">Element Types</div>
                    <div className="text-2xl font-bold text-purple-600">{elementTypes}</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium text-gray-900">Room Labels</div>
                    <div className="text-2xl font-bold text-orange-600">{Object.keys(roomCustomLabels).length}</div>
                  </div>
                </div>
                
                {/* Element Type Breakdown */}
                {totalElements > 0 && (
                  <div className="mt-4">
                    <h4 className="font-medium text-gray-900 mb-2">Element Types:</h4>
                    <div className="flex flex-wrap gap-2 text-sm">
                      {Object.entries(elementBreakdown).map(([type, count]) => (
                        <span key={type} className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {type}: {count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Advanced Repair Options */}
              <div className="mb-6">
                <button
                  onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                  className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-medium"
                >
                  <Settings size={16} />
                  <span>{showAdvancedOptions ? 'Hide' : 'Show'} Advanced Options</span>
                </button>

                {showAdvancedOptions && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-3">Repair Options</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      {Object.entries(repairOptions).map(([key, value]) => (
                        <label key={key} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={value}
                            onChange={(e) => setRepairOptions(prev => ({ ...prev, [key]: e.target.checked }))}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-gray-700 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={handleAnalyze}
                  disabled={isAnalyzing}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAnalyzing ? 'Analyzing...' : 'Analyze File'}
                </button>
                <button
                  onClick={handleReset}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Reset Options
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Analysis Results */}
              <div className="mb-6">
                <div className="flex items-center space-x-2 mb-4">
                  {getSeverityIcon(repairResult.issuesFound.length)}
                  <h3 className="text-lg font-semibold text-gray-900">Analysis Results</h3>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white p-4 rounded-lg border">
                    <div className="text-sm font-medium text-gray-600">Total Issues</div>
                    <div className={`text-2xl font-bold ${getSeverityColor(repairResult.issuesFound.length)}`}>
                      {repairResult.issuesFound.length}
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border">
                    <div className="text-sm font-medium text-gray-600">Wall Issues</div>
                    <div className="text-2xl font-bold text-blue-600">
                      {repairResult.reportSummary.wallsWithIssues}
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border">
                    <div className="text-sm font-medium text-gray-600">Element Issues</div>
                    <div className="text-2xl font-bold text-green-600">
                      {repairResult.reportSummary.elementsWithIssues}
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border">
                    <div className="text-sm font-medium text-gray-600">Fixes Available</div>
                    <div className="text-2xl font-bold text-purple-600">
                      {repairResult.issuesFixed.length}
                    </div>
                  </div>
                </div>

                {/* Detailed Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Wall Metrics */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-3">Wall Repairs</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Temp IDs Generated:</span>
                        <span className="font-medium">{repairResult.reportSummary.tempIdsGenerated}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Duplicates Removed:</span>
                        <span className="font-medium">{repairResult.reportSummary.duplicateWallsRemoved}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Orphans Removed:</span>
                        <span className="font-medium">{repairResult.reportSummary.orphanedWallsRemoved}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Extrusions Fixed:</span>
                        <span className="font-medium">{repairResult.reportSummary.extrusionsFixed}</span>
                      </div>
                    </div>
                  </div>

                  {/* Element Metrics */}
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-green-900 mb-3">Element Repairs</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Element IDs Generated:</span>
                        <span className="font-medium">{repairResult.reportSummary.elementIdsGenerated}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>References Fixed:</span>
                        <span className="font-medium">{repairResult.reportSummary.brokenElementReferencesFixed}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Properties Fixed:</span>
                        <span className="font-medium">{repairResult.reportSummary.elementPropertiesFixed}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Floor Relocations:</span>
                        <span className="font-medium">{repairResult.reportSummary.elementsRelocatedToValidFloors}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Issues Found */}
                {repairResult.issuesFound.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Issues Found:</h4>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                      <ul className="text-sm space-y-1">
                        {repairResult.issuesFound.slice(0, 20).map((issue, index) => (
                          <li key={index} className="text-yellow-800">• {issue}</li>
                        ))}
                        {repairResult.issuesFound.length > 20 && (
                          <li className="text-yellow-600 italic">
                            ... and {repairResult.issuesFound.length - 20} more issues
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Fixes to Apply */}
                {repairResult.issuesFixed.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-2">Fixes to Apply:</h4>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                      <ul className="text-sm space-y-1">
                        {repairResult.issuesFixed.slice(0, 20).map((fix, index) => (
                          <li key={index} className="text-green-800">• {fix}</li>
                        ))}
                        {repairResult.issuesFixed.length > 20 && (
                          <li className="text-green-600 italic">
                            ... and {repairResult.issuesFixed.length - 20} more fixes
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={handleApplyRepair}
                  disabled={repairResult.issuesFixed.length === 0}
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply Repairs ({repairResult.issuesFixed.length} fixes)
                </button>
                <button
                  onClick={() => setRepairResult(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Back to Analysis
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileRepairDialog; 